import type {
  ActionFavoriteItemMutation,
  ActionFavoriteItemMutationVariables
} from './../api/schema/cart';
import { observable, action, configure, makeObservable } from 'mobx';
import API from '@api';
import { queryClient } from '@root/src/providers/query';
import {
  barMenuCategoriesQueryOptions,
  mobileMenuTreeQueryOptions
} from '@queries/catalogMenu';
import { totalFavoritesQueryOptions } from '@queries/favorites';
import { notifications } from '@utils/notifications';
import { IEdetector } from '@utils/browserUTLs';
import type { GetPeopleFavoritesProductsQuery } from '@schema/cart';
import {
  type CatalogMenuItem,
  type CatalogSpecialMenuItem
} from './CatalogStore.type';
import { buildCatalogMenu } from './buildCatalogMenu';

configure({
  enforceActions: 'observed',
  useProxies: IEdetector() ? 'never' : 'always'
});

class Store {
  menu: CatalogMenuItem[] = [];

  specialMenu: CatalogSpecialMenuItem[] = [];

  favoritesItemsAmount = null;

  isExpressEnabled = true;

  catalogState: {
    count?: number | null;
    positionY?: number | null;
    slug?: string | null;
  } | null = null;

  constructor() {
    makeObservable(this, {
      menu: observable,
      specialMenu: observable,
      catalogState: observable,
      favoritesItemsAmount: observable,
      isExpressEnabled: observable,

      getCatalogMenu: action,
      getTotalFavorites: action,
      setFavoritesItemsAmount: action,
      addToFavourites: action,
      setMenu: action,
      setSpecialMenu: action,
      setCatalogState: action,
      setPositionY: action,
      toggleIsExpressEnabled: action,
      setIsExpressEnabled: action
    });
  }

  toggleIsExpressEnabled = (): void => {
    this.isExpressEnabled = !this.isExpressEnabled;
  };

  setIsExpressEnabled = (value: boolean): void => {
    this.isExpressEnabled = value;
  };

  getCatalogMenu = async ({
    is18plus,
    withBarMenu
  }: {
    is18plus?: boolean;
    withBarMenu?: boolean;
  }): Promise<void> => {
    const isDesktop = window.innerWidth >= 1280;
    // ensureQueryData: в пределах staleTime отдаёт из кэша без запроса —
    // autorun в CatalogBtn может дёргать getCatalogMenu сколько угодно
    Promise.all([
      queryClient.ensureQueryData(
        mobileMenuTreeQueryOptions({
          is18plus,
          withChildren: true,
          device: 'desktop'
        })
      ),
      queryClient.ensureQueryData(
        barMenuCategoriesQueryOptions({
          withChildren: true
        })
      )
    ])
      .then(res => {
        // маппинг вынесен в buildCatalogMenu (ЭТАП 2): та же логика нужна
        // CatalogCategory как SSR-фолбэк до инициализации стора
        const catalogMenu = buildCatalogMenu(res[0], res?.[1], {
          isDesktop,
          withBarMenu
        });
        const specialMenu: CatalogSpecialMenuItem[] =
          res?.[0]?.getMenuSpecial?.filter(item => !!item.active) ?? [];
        this.setMenu(catalogMenu);
        this.setSpecialMenu(specialMenu);
      })
      .catch(console.log);
  };

  setMenu = (menu: CatalogMenuItem[]): void => {
    this.menu = menu || [];
  };

  setSpecialMenu = (specialMenu: CatalogSpecialMenuItem[]): void => {
    this.specialMenu = specialMenu || [];
  };

  // Счётчик едет через query-кэш; observable favoritesItemsAmount остаётся
  // зеркалом на переходный период (его наблюдают бейджи в шапке и кабинете)
  getTotalFavorites = async (): Promise<
    GetPeopleFavoritesProductsQuery | undefined
  > =>
    await queryClient.fetchQuery(totalFavoritesQueryOptions()).then(res => {
      if (!res.getPeopleFavoritesProducts) return;

      const amount = res.getPeopleFavoritesProducts.totalCount;
      this.setFavoritesItemsAmount(amount);
      return res;
    });

  setFavoritesItemsAmount = (favoritesItemsAmount): void => {
    this.favoritesItemsAmount = favoritesItemsAmount || '';
  };

  addToFavourites = async (
    obj: ActionFavoriteItemMutationVariables
  ): Promise<ActionFavoriteItemMutation> =>
    await API.actionFavoriteItem(obj).then(res => {
      const amount = res.actionFavoriteItem?.total;
      this.setFavoritesItemsAmount(amount);
      queryClient.invalidateQueries({
        queryKey: totalFavoritesQueryOptions().queryKey
      });
      if (obj.action === 'remove') {
        notifications('Товар успешно удален из избранного', '');
      } else if (obj.action === 'add') {
        notifications('Товар успешно добавлен в избранное', '');
      }
      return res;
    });

  setCatalogState = (count: number | null, slug?: string | null): void => {
    this.catalogState = {
      ...this.catalogState,
      count,
      ...(slug !== undefined ? { slug } : {})
    };
  };

  setPositionY = (positionY: number | null): void => {
    this.catalogState = {
      ...this.catalogState,
      positionY
    };
  };
}

const catalogStore = new Store();
export type CatalogStoreType = typeof catalogStore;
export default catalogStore;
